/**
 * Converts interview data to form data format for the background information form
 * @param {Object} interview - The interview object containing question answers
 * @param {Object} interview.questionAnswers - Object containing question type keys and answer objects
 * @returns {Object} Form data object with mapped properties for background information form
 */
export function interviewToFormData(interview) {
    /**
     * Helper function to extract answer from interview question answers
     * @param {string} questionType - The type/key of the question
     * @returns {string|undefined} The answer string or undefined if not found
     */
    const answer = questionType => interview?.questionAnswers?.[questionType]?.answer;

    return {
        hasFiledForBankruptcy: answer("BankruptcyFiledOrDischarged"),
        hasFiledForBankruptcyComment: answer("BankruptcyFiledOrDischargedComment"),
        hasDelinquentUnpaidDebts: answer("DelinquentUnpaidDebts"),
        hasDelinquentUnpaidDebtsComment: answer("DelinquentUnpaidDebtsComment"),
        hasBeenSubjectOfAnAdministrativeProceeding: answer("AdministrativeProceeding"),
        hasBeenSubjectOfAnAdministrativeProceedingComment: answer("AdministrativeProceedingComment"),
        hasLicenseBeenSuspended: answer("InsuranceLicenseSuspended"),
        hasLicenseBeenSuspendedComment: answer("InsuranceLicenseSuspendedComment"),
        hasHadTerminatedContractFromMisconduct: answer("InsuranceAgencyContractTerminated"),
        hasHadTerminatedContractFromMisconductComment: answer("InsuranceAgencyContractTerminatedComment"),
        hasPriorConviction: answer("ConvictedOrCharged"),
        hasPriorConvictionComment: answer("ConvictedOrChargedComment"),
        isSubjectOfInvestigation: answer("SubjectOfComplaint"),
        isSubjectOfInvestigationComment: answer("SubjectOfComplaintComment"),
        hasBusinessRelationshipWithGEICOEmployee: answer("BusinessRelationshipWithGeico"),
        hasBusinessRelationshipWithGEICOEmployeeComment: answer("BusinessRelationshipWithGeicoComment")
    };
}

/**
 * Converts form data to question answers format for interview processing
 * @param {Object} formData - The form data object containing background information responses
 * @param {boolean} formData.hasFiledForBankruptcy - Whether the applicant has filed for bankruptcy
 * @param {boolean} formData.hasDelinquentUnpaidDebts - Whether the applicant has delinquent unpaid debts
 * @param {boolean} formData.hasBeenSubjectOfAnAdministrativeProceeding - Whether the applicant has been subject of an administrative proceeding
 * @param {boolean} formData.hasLicenseBeenSuspended - Whether the applicant's license has been suspended
 * @param {boolean} formData.hasHadTerminatedContractFromMisconduct - Whether the applicant has had a terminated contract due to misconduct
 * @param {boolean} formData.hasPriorConviction - Whether the applicant has a prior conviction
 * @param {boolean} formData.isSubjectOfInvestigation - Whether the applicant is currently subject of an investigation
 * @param {boolean} formData.hasBusinessRelationshipWithGEICOEmployee - Whether the applicant has a business relationship with a GEICO employee
 * @returns {Object} Question answers object with mapped properties for interview processing
 */
export function formDataToQuestionAnswers(formData) {
    if (!formData) return {};

    /**
     * Helper function to map a value to question answer format if it exists
     * @param {string} key - The question key/type
     * @param {*} value - The value to map
     * @returns {Object|{}} Question answer object or empty object if value doesn't exist
     */
    const mapIfExists = (key, value) =>
        value !== null && value !== undefined
            ? { [key]: { answer: value.toString() } }
            : {};

    /**
     * Helper function to map a comment value only if the associated yes/no question is "true"
     * @param {string} commentKey - The comment question key/type
     * @param {*} commentValue - The comment value to map
     * @param {bool} associatedAnswer - The associated yes/no question value
     * @returns {Object|{}} QuestionAnswer object or empty object
     */
    const mapCommentIfYes = (commentKey, commentValue, associatedAnswer) => {
        const noAnswerResult = associatedAnswer === false ? { [commentKey]: null } : {};

        return associatedAnswer === true && commentValue !== null && commentValue !== undefined
            ? { [commentKey]: { answer: commentValue.toString() } }
            : noAnswerResult;
    };


    return {
        ...mapIfExists("BankruptcyFiledOrDischarged", formData.hasFiledForBankruptcy),
        ...mapCommentIfYes("BankruptcyFiledOrDischargedComment", formData.hasFiledForBankruptcyComment, formData.hasFiledForBankruptcy),
        ...mapIfExists("DelinquentUnpaidDebts", formData.hasDelinquentUnpaidDebts),
        ...mapCommentIfYes("DelinquentUnpaidDebtsComment", formData.hasDelinquentUnpaidDebtsComment, formData.hasDelinquentUnpaidDebts),
        ...mapIfExists("AdministrativeProceeding", formData.hasBeenSubjectOfAnAdministrativeProceeding),
        ...mapCommentIfYes("AdministrativeProceedingComment", formData.hasBeenSubjectOfAnAdministrativeProceedingComment, formData.hasBeenSubjectOfAnAdministrativeProceeding),
        ...mapIfExists("InsuranceLicenseSuspended", formData.hasLicenseBeenSuspended),
        ...mapCommentIfYes("InsuranceLicenseSuspendedComment", formData.hasLicenseBeenSuspendedComment, formData.hasLicenseBeenSuspended),
        ...mapIfExists("InsuranceAgencyContractTerminated", formData.hasHadTerminatedContractFromMisconduct),
        ...mapCommentIfYes("InsuranceAgencyContractTerminatedComment", formData.hasHadTerminatedContractFromMisconductComment, formData.hasHadTerminatedContractFromMisconduct),
        ...mapIfExists("ConvictedOrCharged", formData.hasPriorConviction),
        ...mapCommentIfYes("ConvictedOrChargedComment", formData.hasPriorConvictionComment, formData.hasPriorConviction),
        ...mapIfExists("SubjectOfComplaint", formData.isSubjectOfInvestigation),
        ...mapCommentIfYes("SubjectOfComplaintComment", formData.isSubjectOfInvestigationComment, formData.isSubjectOfInvestigation),
        ...mapIfExists("BusinessRelationshipWithGeico", formData.hasBusinessRelationshipWithGEICOEmployee),
        ...mapCommentIfYes("BusinessRelationshipWithGeicoComment", formData.hasBusinessRelationshipWithGEICOEmployeeComment, formData.hasBusinessRelationshipWithGEICOEmployee)
    };
}
